import { BariatricProcedureStage, BariatricProcedureStageLabel } from '@bariendo/lib/constants';
import { BARIATRIC_PROCEDURE_STAGE_EXTENSION } from '@bariendo/lib/extensions';
import { Flex, Group, Text } from '@mantine/core';
import { formatHumanName, getExtension, isReference } from '@medplum/core';
import { Patient, Reference } from '@medplum/fhirtypes';
import { HumanNameDisplay, ResourceAvatar, ResourceInput } from '@medplum/react';
import { useEffect, useState } from 'react';
import { formatBirthDate } from '@/services/patient';

interface PatientCardProps {
  patient?: Reference<Patient> | Patient;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  usePatientCard?: boolean;
  setSelectedPatient: (patient: Patient | undefined) => void;
}

const getDisplayName = (patient: Patient | Reference<Patient>): string => {
  const isRef = isReference(patient);
  const isPatientResource = 'name' in patient;
  if (isRef) {
    return patient.display ?? 'Unknown Patient';
  } else if (isPatientResource) {
    return formatHumanName(patient.name?.[0]);
  } else {
    return 'Unknown Patient';
  }
};

function PatientSelector({ resource }: { resource: Patient }) {
  const stage = getExtension(resource, BARIATRIC_PROCEDURE_STAGE_EXTENSION)?.valueString as
    | BariatricProcedureStage
    | undefined;
  const stageLabel = stage ? BariatricProcedureStageLabel[stage] : undefined;

  return (
    <Flex gap="md" align="center">
      <ResourceAvatar value={resource} radius="xl" />
      <Flex direction="column">
        <HumanNameDisplay value={resource?.name?.[0]} />
        <Text size="xs" mr="xs" c="dimmed">
          {[formatBirthDate(resource?.birthDate), stageLabel].filter(Boolean).join(' • ')}
        </Text>
      </Flex>
    </Flex>
  );
}

export default function PatientCard({
  patient,
  setSelectedPatient,
  disabled,
  label,
  placeholder,
  usePatientCard = true,
}: PatientCardProps) {
  const [isPatientCardOpen, setIsPatientCardOpen] = useState(false);

  const handleOnChangePatient = (resource: Patient | undefined) => {
    setSelectedPatient(resource);
    if (resource && usePatientCard) {
      setIsPatientCardOpen(true);
    }
  };

  useEffect(() => {
    if (patient && usePatientCard) {
      setIsPatientCardOpen(true);
    }
  }, [patient, usePatientCard]);

  if (isPatientCardOpen && patient) {
    const displayName = getDisplayName(patient);
    return (
      <Group onClick={() => setIsPatientCardOpen(false)} style={{ cursor: 'pointer' }}>
        <ResourceAvatar value={patient} size="sm" style={{ flexShrink: 0, marginTop: '4px' }} />
        <Text
          variant="section"
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '150px',
          }}
        >
          {displayName}
        </Text>
      </Group>
    );
  }

  return (
    <ResourceInput
      name="patient"
      resourceType="Patient"
      onChange={handleOnChangePatient}
      placeholder={placeholder ?? 'Select patient'}
      label={label}
      defaultValue={patient}
      itemComponent={(props: { resource: Patient }) => <PatientSelector resource={props.resource} />}
      disabled={disabled}
    />
  );
}
